/**
 * CapacityBar — Team capacity overview per day
 * Shows available vs scheduled vs on leave per day of the week
 */

import { useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { Staff, LeaveEntry } from '../../stores/workforce-store';
import type { ScheduleEntry, AvailabilityEntry } from '../../stores/schedule-store';
import { filterSickLeave } from '../../lib/leave-types';

interface DayCapacity {
  date: string;
  available: number;
  /** Deel van `available` dat maar een dagdeel kan. Telt mee als kop, niet als hele dag. */
  partial: number;
  scheduled: number;
  onLeave: number;
  sick: number;
  gap: number;
}

interface CapacityBarProps {
  staffList: Staff[];
  schedule: ScheduleEntry[];
  leaveEntries: LeaveEntry[];
  availability: AvailabilityEntry[];
  weekDates: string[];
}

export function CapacityBar({ staffList, schedule, leaveEntries, availability, weekDates }: CapacityBarProps) {
  const capacityData = useMemo((): DayCapacity[] => {
    const activeStaff = staffList.filter(s => s.is_active);

    return weekDates.map(date => {
      const d = new Date(date + 'T00:00:00');
      const dow = d.getDay();

      // Available: staff with werkrooster for this day + no explicit unavailability
      let availableCount = 0;
      let partialCount = 0;
      for (const staff of activeStaff) {
        const hasRooster = staff.werkrooster?.some(w => w.day === dow);
        const avail = availability.find(a => a.staff_id === staff.id && a.date === date);
        if (avail && !avail.is_available) continue;
        if (hasRooster || (avail && avail.is_available)) {
          availableCount++;
          // Een dagdeel is een kop, geen dag. Apart tellen, anders leest
          // "5 beschikbaar" als vijf hele dagen terwijl er twee alleen 's
          // avonds kunnen — en dan plant kantoor een ochtendroute op lucht.
          if (avail?.time_from || avail?.time_to) partialCount++;
        }
      }

      // Scheduled: unique staff with schedule entries for this day
      const scheduledStaff = new Set(
        schedule.filter(s => s.date === date && s.staff_id).map(s => s.staff_id)
      );

      // On leave
      const leaveStaff = new Set(
        leaveEntries.filter(e => e.date === date).map(e => e.staff_id)
      );
      const sickStaff = new Set(
        filterSickLeave(leaveEntries.filter(e => e.date === date)).map(e => e.staff_id)
      );

      return {
        date,
        available: availableCount,
        partial: partialCount,
        scheduled: scheduledStaff.size,
        onLeave: leaveStaff.size,
        sick: sickStaff.size,
        gap: availableCount - scheduledStaff.size - leaveStaff.size,
      };
    });
  }, [staffList, schedule, leaveEntries, availability, weekDates]);

  const MIN_WASSERIJ = 3;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
        Capaciteit
      </h3>
      <div className="grid grid-cols-7 gap-2">
        {capacityData.map(day => {
          const maxBar = Math.max(day.available, day.scheduled + day.onLeave, 1);
          const scheduledPct = (day.scheduled / maxBar) * 100;
          const isUnder = day.scheduled < MIN_WASSERIJ;

          return (
            <div key={day.date} className="text-center">
              <p className="text-xs font-medium text-slate-500 mb-1">
                {format(parseISO(day.date), 'EEE d', { locale: nl })}
              </p>
              {/* Bar */}
              <div className="h-16 bg-slate-100 rounded relative flex flex-col-reverse overflow-hidden">
                <div
                  className={`rounded transition-all ${isUnder ? 'bg-red-300' : 'bg-indigo-400'}`}
                  style={{ height: `${Math.min(100, scheduledPct)}%` }}
                />
              </div>
              {/* Count */}
              <p className={`text-sm font-bold mt-1 ${isUnder ? 'text-red-600' : 'text-main'}`}>
                {day.scheduled}/{day.available}
              </p>
              {day.partial > 0 && (
                <p className="text-[10px] text-emerald-600">{day.partial} dagdeel</p>
              )}
              {day.onLeave > 0 && (
                <p className="text-[10px] text-amber-600">{day.onLeave} verlof</p>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-2 text-[10px] text-muted">
        <span>Ingepland / Beschikbaar</span>
        {capacityData.some(d => d.scheduled < MIN_WASSERIJ) && (
          <span className="text-red-500">Rood = minder dan {MIN_WASSERIJ} ingepland</span>
        )}
      </div>
    </div>
  );
}
